import {CommonDecisionTreeNodeType, MacroDecisionTreeConditionType} from '@frontapp/workflows/decision_trees';
import {ExpressionId} from '@frontapp/workflows/expressions';
import _, {isEqual} from 'lodash';
import {useCallback, useEffect, useRef, useState} from 'react';
import {Edge, XYPosition} from 'reactflow';

import {
  makeMacroFlowBuilderDefaultNodeData,
  makeMacroFlowBuilderNode,
} from '../../../../../libs/shared/core/src/helpers/models/macroFlowBuilderHelpers';
import {
  DecisionTreeNode,
  DecisionTreeNodeTypesEnum,
} from '../../../../../libs/shared/core/src/helpers/types/decisionTreeTypes';
import {
  ClientMacroDecisionTreeCondition,
  ClientMacroDecisionTreeNodeAction,
  ClientMacroDecisionTreeNodeActions,
  ClientMacroDecisionTreeNodeBranchByConditions,
  ClientMacroDecisionTreeNodeData,
  MacroFlowBuilderNodeData,
} from '../../../../../libs/shared/core/src/helpers/types/macroFlowBuilderTypes';
import {isDecisionTreeBranchNodeDataMacro} from '../../../../../libs/shared/core/src/models/api/decisionTrees/decisionTreeNodeDataMacroModel';
import {useDecisionTreeStore} from '../../../../../libs/shared/ui/src/components/decisionTrees/decisionTreeBuilder/hooks/useDecisionTreeStore';
import {useFlowBuilderDirtyStore} from '../../../../../libs/shared/ui/src/components/decisionTrees/flowBuilder/hooks/useFlowBuilderDirtyStore';
import {useFlowBuilderHistory} from '../../../../../libs/shared/ui/src/components/decisionTrees/flowBuilder/hooks/useFlowBuilderHistory';
import {Expression} from '../../../../settings/editors/expressions/expressionModel';
import {ExpressionDependencies} from '../../../../settings/editors/expressions/expressionState';
import {
  InferredType,
  MacroStartingPointResult,
} from '../../../../settings/editors/expressions/infer/internal';
import {maybeRemoveExpressionReferenceFromConditionOrAction} from '../../../../settings/rules/ruleEditor/state/ruleEditorReducerHelpers';
import {useRuleIntegrationContext} from '../../../../settings/rules/ruleIntegrationContext';
import {RuleIntegration} from '../../../../settings/rules/ruleIntegrationModel';
import {applyDirtyExpressionsEditorActions} from '../../flowBuilder/expressionHelpers';
import {useFlowBuilderExpressionsEditor} from '../../flowBuilder/expressionHooks';
import {MacroFlowBuilderSelectedNode, MacroFlowBuilderState, MacroFlowBuilderStore} from '../types';

/*
 * Hooks.
 */

export function useMacroFlowBuilderStore(
  initialNodes: Array<DecisionTreeNode<MacroFlowBuilderNodeData>>,
  initialEdges: Array<Edge>,
  initialExpressions: ReadonlyArray<Expression>,
  expressionDependencies: ExpressionDependencies,
): MacroFlowBuilderStore {
  const decisionTreeStore = useDecisionTreeStore<MacroFlowBuilderNodeData>(initialNodes, initialEdges);

  const [selectedNode, setSelectedNode] = useState<MacroFlowBuilderSelectedNode | undefined>(undefined);
  const {ruleIntegration} = useRuleIntegrationContext();

  const expressionsEditor = useFlowBuilderExpressionsEditor(
    initialExpressions,
    MacroStartingPointResult,
    expressionDependencies,
    {
      onExpressionDeleted: (expressionId) => {
        const newNodes = validateNodesWithExpression(
          decisionTreeStore.nodes,
          expressionId,
          null,
          ruleIntegration,
        );

        if (!isEqual(decisionTreeStore.nodes, newNodes)) {
          decisionTreeStore.setNodes(newNodes);
        }
      },
      onExpressionUpdated: (expression) => {
        const newNodes = validateNodesWithExpression(
          decisionTreeStore.nodes,
          expression.id,
          expression.inferredType,
          ruleIntegration,
        );

        if (!isEqual(decisionTreeStore.nodes, newNodes)) {
          decisionTreeStore.setNodes(newNodes);
        }
      },
    },
  );

  // Store state slices in state for any change to allow popping slices off from most recent to older.
  const [currentStateSlice, setCurrentStateSlice] = useState<MacroFlowBuilderState>({
    nodes: decisionTreeStore.nodes,
    edges: decisionTreeStore.edges,
    expressions: expressionsEditor.state.expressions,
  });

  useEffect(() => {
    // Wait until transitioning is complete before storing a state slice.
    if (decisionTreeStore.isTransitioning) {
      return;
    }

    // We do not want changes in the native React Flow "selected" property to trigger a re-computation.
    const decisionTreeStoreNodes = decisionTreeStore.nodes.map(({selected, ...node}) => node);
    const currentStateSliceNodes = currentStateSlice.nodes.map(({selected, ...node}) => node);

    if (
      _.isEqual(decisionTreeStoreNodes, currentStateSliceNodes) &&
      _.isEqual(decisionTreeStore.edges, currentStateSlice.edges) &&
      _.isEqual(expressionsEditor.state.expressions, currentStateSlice.expressions)
    ) {
      return;
    }

    setCurrentStateSlice({
      nodes: decisionTreeStore.nodes,
      edges: decisionTreeStore.edges,
      expressions: expressionsEditor.state.expressions,
    });
  }, [
    currentStateSlice.edges,
    currentStateSlice.expressions,
    currentStateSlice.nodes,
    decisionTreeStore.edges,
    decisionTreeStore.nodes,
    expressionsEditor.state.expressions,
    decisionTreeStore.isTransitioning,
  ]);

  // This callback must be stable in order for history to be stable.
  const onReplaceExpressionsRef = useRef(expressionsEditor.actions.onReplaceExpressions);
  const historyNavCallback = useCallback((poppedStateSlice: MacroFlowBuilderState) => {
    // Set expressions to the previous state.
    onReplaceExpressionsRef.current(poppedStateSlice.expressions);
  }, []);

  const history = useFlowBuilderHistory(decisionTreeStore, currentStateSlice, historyNavCallback);

  const dirtyStore = useFlowBuilderDirtyStore(
    decisionTreeStore,
    isDecisionTreeBranchNodeDataMacro,
    makeEndNodeAtPosition,
  );

  const [nodeIdsWithErrors, setNodeIdsWithErrors] = useState<ReadonlyArray<string>>([]);

  const macroFlowBuilderStore: MacroFlowBuilderStore = {
    ...decisionTreeStore,
    selectedNode,
    setSelectedNode,
    history,
    ...dirtyStore,
    expressionsEditor: applyDirtyExpressionsEditorActions(
      expressionsEditor,
      dirtyStore.setIsDirty,
      history.push,
    ),
    nodeIdsWithErrors,
    setNodeIdsWithErrors,
  };

  return macroFlowBuilderStore;
}

/*
 * Helpers.
 */

function makeEndNodeAtPosition(position: XYPosition) {
  return makeMacroFlowBuilderNode(
    DecisionTreeNodeTypesEnum.PLACEHOLDER,
    position,
    makeMacroFlowBuilderDefaultNodeData(CommonDecisionTreeNodeType.ACTION),
  );
}

function validateNodesWithExpression(
  nodes: Array<DecisionTreeNode<ClientMacroDecisionTreeNodeData>>,
  expressionId: ExpressionId,
  inferredType: InferredType | null,
  ruleIntegration: RuleIntegration,
): Array<DecisionTreeNode<ClientMacroDecisionTreeNodeData>> {
  return nodes.map((node) => {
    switch (node.data.type) {
      case CommonDecisionTreeNodeType.BRANCH_BY_CONDITION: {
        return {
          ...node,
          data: validateBranchByConditionNodeData(node.data, expressionId, inferredType, ruleIntegration),
        };
      }

      case CommonDecisionTreeNodeType.ACTION: {
        return {
          ...node,
          data: validateActionNodeData(node.data, expressionId, inferredType, ruleIntegration),
        };
      }

      default:
        return node;
    }
  });
}

function validateBranchByConditionNodeData(
  data: ClientMacroDecisionTreeNodeBranchByConditions,
  expressionId: ExpressionId,
  inferredType: InferredType | null,
  ruleIntegration: RuleIntegration,
): ClientMacroDecisionTreeNodeBranchByConditions {
  return {
    ...data,
    options: data.options.map((option) => {
      const [and, ...conditions] = option.conditions;

      const validatedConditions = maybeRemoveExpressionReferenceFromConditionOrAction<
        ClientMacroDecisionTreeCondition<MacroDecisionTreeConditionType> & {integration_id: number}
      >(wrapWithDefaultIntegration(conditions), ruleIntegration, {
        referenceId: expressionId,
        inferredType: inferredType ?? undefined,
      });

      const normalizedConditions = validatedConditions.map(
        (condition): ClientMacroDecisionTreeCondition<MacroDecisionTreeConditionType> => ({
          type: condition.type,
          params: condition.params,
          clientId: condition.clientId,
        }),
      );

      return {
        ...option,
        conditions: [and, ...normalizedConditions],
      };
    }),
  };
}

function validateActionNodeData(
  data: ClientMacroDecisionTreeNodeActions,
  expressionId: ExpressionId,
  inferredType: InferredType | null,
  ruleIntegration: RuleIntegration,
): ClientMacroDecisionTreeNodeActions {
  const validatedActions = maybeRemoveExpressionReferenceFromConditionOrAction<
    ClientMacroDecisionTreeNodeAction & {integration_id: 0}
  >(wrapWithDefaultIntegration(data.actions), ruleIntegration, {
    referenceId: expressionId,
    inferredType: inferredType ?? undefined,
  });

  const normalizedActions = validatedActions.map(
    (action): ClientMacroDecisionTreeNodeAction =>
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      ({
        clientId: action.clientId,
        params: action.params,
        type: action.type,
      }) as ClientMacroDecisionTreeNodeAction,
  );

  return {
    ...data,
    actions: normalizedActions,
  };
}

function wrapWithDefaultIntegration<T>(items: ReadonlyArray<T>): ReadonlyArray<T & {integration_id: 0}> {
  return items.map((item) => ({...item, integration_id: 0}));
}
